import { AvatarCarousel } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/AvatarCarousel";
import { BrandTileCarousel } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/BrandTileCarousel";
import { DynamicAutoColumnGrid } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/DynamicAutoColumnGrid";
import { DynamicSparseGrid } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/DynamicSparseGrid";
import { FlashSaleCarousel } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/FlashSaleCarousel";
import { InsightsCarousel } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/InsightsCarousel";
import { MostPopularBrandsCarousel } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/MostPopularBrandsCarousel";
import { NoOpLayoutElement } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/NoOpLayoutElement";
import { ProductWithBrandCarousel } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/ProductWithBrandCarousel";
import { SampleRequestCarousel } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/SampleRequestCarousel";
import { SearchIconPills } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/SearchIconPills";
import { SearchPills } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/SearchPills";
import { SearchTiles } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/SearchTiles";
import { SmallProductTileCarousel } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/SmallProductTileCarousel";
import { TallCategoryTiles } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/TallCategoryTiles";
import { VerticalExpandingTiles } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/VerticalExpandingTiles";
import { BaseCarouselProps } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/CarouselBase";
import { Context } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { getMilestoneInteractionsByType } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Utils";
import { assignSettingOnEvent } from "@faire/retailer-visitor-shared/settings/assignSettingOnEvent";
import { useRecordMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import * as React from "react";
import styled from "styled-components";

export namespace LayoutElementCarouselProps {
  export const build = (
    partial?: Partial<BaseCarouselProps> | undefined
  ): BaseCarouselProps => ({
    ...(partial ?? {}),
  });
}

export const LayoutElementCarousel: React.FC<BaseCarouselProps> = (props) => {
  const recordMilestone = useRecordMilestone();
  const { layoutElement } = React.useContext(Context);
  const onViewRef = useDoActionWhenVisible(() => {
    getMilestoneInteractionsByType(layoutElement, "RECORD_UPON_SEEN").forEach(
      (milestone) => {
        recordMilestone(milestone);
      }
    );
    layoutElement.layout_element_data_assign_settings_on_client_view?.forEach(
      (setting) => assignSettingOnEvent(setting)
    );
  });

  if (!layoutElement.type) {
    return null;
  }

  const CarouselComponent = carouselComponentMap.get(layoutElement.type);

  if (!CarouselComponent) {
    return null;
  }

  return (
    <CarouselWrapper ref={onViewRef}>
      <CarouselComponent {...props} />
    </CarouselWrapper>
  );
};

export const carouselComponentMap = new Map<
  keyof typeof ILayoutElement.Type | undefined,
  React.FC<BaseCarouselProps>
>([
  [ILayoutElement.Type.AVATAR_CAROUSEL, AvatarCarousel],
  [ILayoutElement.Type.AVATAR_CAROUSEL_WITH_GRID, AvatarCarousel],
  [ILayoutElement.Type.BUY_FROM_SAMPLES_CAROUSEL, ProductWithBrandCarousel],
  [ILayoutElement.Type.DYNAMIC_GRID_OR_CAROUSEL, DynamicAutoColumnGrid],
  [ILayoutElement.Type.DYNAMIC_SPARSE_GRID, DynamicSparseGrid],
  [ILayoutElement.Type.HOMEPAGE_SEARCH_QUERIES_BANNER, SearchTiles],
  [
    ILayoutElement.Type.LEAN_PRODUCT_WITH_BRAND_TILE_CAROUSEL,
    ProductWithBrandCarousel,
  ],
  [ILayoutElement.Type.MOST_POPULAR_BRANDS, MostPopularBrandsCarousel],
  [ILayoutElement.Type.NO_OP_LAYOUT_ELEMENT, NoOpLayoutElement],
  [ILayoutElement.Type.PRODUCT_TILE_CAROUSEL, ProductWithBrandCarousel],
  [ILayoutElement.Type.PRODUCT_PAGE_HEADER_CAROUSEL, SmallProductTileCarousel],
  [ILayoutElement.Type.RECENT_SEARCHES_CAROUSEL, SearchIconPills],
  [ILayoutElement.Type.RETAILER_BRAND_INSIGHTS_CAROUSEL, InsightsCarousel],
  [ILayoutElement.Type.SAMPLE_REQUEST_CAROUSEL, SampleRequestCarousel],
  [ILayoutElement.Type.SEARCH_FILTER_PILLS, SearchPills],
  [ILayoutElement.Type.SMALL_FLASH_SALE_CAROUSEL, FlashSaleCarousel],
  [ILayoutElement.Type.SMALL_MAKER_TILE_CAROUSEL, BrandTileCarousel],
  [ILayoutElement.Type.TALL_CATEGORY_TILE, TallCategoryTiles],
  [
    ILayoutElement.Type.VERTICALLY_EXPANDABLE_PRODUCT_GRID,
    VerticalExpandingTiles,
  ],
  [ILayoutElement.Type.WAREHOUSE_SALE_CAROUSEL, FlashSaleCarousel],
]);

const CarouselWrapper = styled.div`
  flex: 1;
  min-width: 0;
  &:empty ~ div {
    display: none;
  }
  > span {
    width: 100%;
  }

  ${responsiveMediaQueries.mobile} {
    overflow-x: hidden;
  }
`;
